import { useAutocompleteStyles } from '@app/src/components/Form/Select/SimpleSelect'
import TextField from '@app/src/components/Ui/TextField'
import type { EntitySearchAssignProps } from '@app/src/pages/TabularImport/shared/components/EntitySearchAssignProps'
import { Search } from '@mui/icons-material'
import { Autocomplete, Box, Chip, CircularProgress, InputAdornment, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useDebounce } from 'use-debounce'

const SEARCH_DEBOUNCE_MS = 500
const MIN_QUERY_LENGTH = 2

export type EntitySearchOption = { id: number; name: string }

type Props<TOption extends EntitySearchOption> = EntitySearchAssignProps & {
  useSearch: (query: string, enabled: boolean) => { items: TOption[]; isFetching: boolean }
  // Precedes the id wherever it is shown, so providers read "WF #12" and products "#12".
  idPrefix: string
  getOptionDetail: (option: TOption) => string | undefined
  placeholder: string
  name: string
}

function EntitySearchAssign<TOption extends EntitySearchOption>({
  assignedEntityId,
  assignedEntityName,
  onAssign,
  onRemove,
  useSearch,
  idPrefix,
  getOptionDetail,
  placeholder,
  name,
}: Props<TOption>) {
  const classes = useAutocompleteStyles()
  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery] = useDebounce(inputValue, SEARCH_DEBOUNCE_MS)

  const { items: searchResults, isFetching } = useSearch(debouncedQuery, debouncedQuery.length >= MIN_QUERY_LENGTH)

  if (assignedEntityId) {
    const id = `${idPrefix}${assignedEntityId}`
    const label = assignedEntityName ? `→ ${assignedEntityName} (${id})` : `→ ${id}`
    return (
      <Chip label={label} size="small" color="primary" variant="outlined" onDelete={onRemove} sx={{ maxWidth: 280 }} />
    )
  }

  return (
    <Autocomplete<TOption, false, false, true>
      freeSolo
      options={searchResults}
      getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, value) => {
        if (value && typeof value !== 'string') {
          onAssign(value.id)
          setInputValue('')
        }
      }}
      filterOptions={x => x}
      classes={{ clearIndicator: classes.clearIndicator, popupIndicator: classes.popupIndicator, paper: classes.paper }}
      renderOption={(props, option) => {
        const detail = getOptionDetail(option)
        return (
          <Box component="li" {...props} key={option.id}>
            <Box>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {detail ? `${detail} · ` : ''}
                {idPrefix}
                {option.id}
              </Typography>
            </Box>
          </Box>
        )
      }}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          name={name}
          size="small"
          placeholder={placeholder}
          sx={{ minWidth: 220 }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isFetching && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default EntitySearchAssign
